import React, { useCallback, useMemo, useRef, useState } from "react"

import {
  buildMetricDashboardSourcesById,
  useMetricsList,
  type MetricDashboardSource,
} from "@/metrics/catalog"
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@coval/ui"
import {
  Activity,
  Bell,
  Copy,
  Download,
  GripVertical,
  Info,
  MoreVertical,
  Settings,
  Trash2,
} from "lucide-react"

import { logger } from "@/lib/logger"
import { cn } from "@/utils/cn"

import type { ChartWidgetConfig } from "../../types/chart"
import {
  AGGREGATION_TYPE,
  type DashboardWidgetConfig,
  type DashboardWidgetProps,
} from "../../types/core"
import {
  convertTableWidgetDataToCSV,
  convertWidgetDataToCSV,
  downloadWidgetCSV,
  generateWidgetCSVFilename,
} from "../../utils/widgetCsvExport"
import type { WidgetExportData } from "../BaseWidget"
import { ClickableMetricTitle } from "../ClickableMetricTitle"
import { useWidgetDragHandle } from "./WidgetDragHandleContext"
import { WidgetWidthMenu } from "./WidgetWidthMenu"

const log = logger("GridWidgetContainer")

/**
 * The card's drag target is its whole title bar, as in Datadog: a strip across
 * the top of the card holding the title and the action buttons. The strip
 * reaches into the card's padding so it runs edge to edge and is as tall as a
 * title bar, and it tints on hover so the handle reads as one piece. The
 * buttons inside it keep their own presses; the grid's sensor sees to that.
 *
 * The bar itself is a plain container: giving it a button role would nest the
 * title link and the action buttons inside another control. The pointer
 * listener goes on the bar, so a pull anywhere on it drags, while the keyboard
 * listener and the sortable's ARIA attributes go on a handle of their own that
 * is only visible while it has focus, so keyboard reordering keeps working.
 *
 * Outside a sortable grid — the widget configuration preview, for one — there
 * is nothing to drag, so the bar renders as a plain wrapper.
 */
const WidgetDragBar: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className, children }) => {
  const { attributes, listeners, setActivatorNodeRef } = useWidgetDragHandle()

  if (!listeners) {
    return <div className={className}>{children}</div>
  }

  const { onKeyDown, ...pointerListeners } = listeners
  const keyboardListeners: typeof listeners = { onKeyDown }

  return (
    <div
      {...pointerListeners}
      className={cn(
        "widget-drag-handle cursor-grab transition-colors active:cursor-grabbing",
        "hover:bg-surface-default-hover",
        className,
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...keyboardListeners}
        aria-label="Drag to reorder widget"
        className={cn(
          "sr-only shrink-0 focus-visible:not-sr-only focus-visible:inline-flex focus-visible:size-6 focus-visible:items-center focus-visible:justify-center",
          "focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active",
        )}
      >
        <GripVertical className="h-3.5 w-3.5 text-coval-text-muted" />
      </button>
      {children}
    </div>
  )
}

interface WidgetContainerChildProps {
  widget: DashboardWidgetProps["widget"] & {
    metric?: MetricDashboardSource | null
    onConfigChange?: (newConfig: DashboardWidgetConfig) => void
  }
  onExportDataReady?: (data: WidgetExportData | null) => void
}

export const GridWidgetContainer: React.FC<
  DashboardWidgetProps & {
    onAlert?: (widgetId: string) => void
  }
> = ({
  widget,
  onRemove,
  onConfigure,
  onConfigChange,
  onClone,
  onAlert,
  onWidthChange,
  children,
}) => {
  const metricTitleRef = useRef<HTMLButtonElement>(null)
  const exportDataRef = useRef<WidgetExportData | null>(null)
  const [hasExportData, setHasExportData] = useState(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false)

  const metricId = useMemo(
    () => widget.config?.metricId,
    [widget.config?.metricId],
  )

  const { data: metricRecords } = useMetricsList({
    metricNames: metricId ? [metricId] : undefined,
    enabled: Boolean(metricId),
  })

  const selectedMetric = useMemo(() => {
    if (!metricRecords || !metricId) {
      return null
    }
    return Object.values(metricRecords).find((metric) => metric.id === metricId)
  }, [metricRecords, metricId])

  const selectedDashboardMetric = useMemo(() => {
    if (!selectedMetric) {
      return null
    }

    return (
      buildMetricDashboardSourcesById({ [selectedMetric.id]: selectedMetric })[
        selectedMetric.id
      ] ?? null
    )
  }, [selectedMetric])

  const handleDownloadCSV = useCallback(() => {
    const exportData = exportDataRef.current
    if (!exportData) {
      return
    }

    try {
      const config = widget.config as ChartWidgetConfig
      const widgetTitle = config.title || widget.title || "Widget"

      // Handle table widget export (check for table-specific properties)
      if (exportData.widgetType === "table" && exportData.tableData) {
        const csvContent = convertTableWidgetDataToCSV({
          widgetTitle,
          tableData: exportData.tableData,
          dateRange: exportData.dateRange,
        })

        const filename = generateWidgetCSVFilename(
          widgetTitle,
          "table",
          AGGREGATION_TYPE.SUCCESS,
          exportData.dateRange,
        )

        downloadWidgetCSV(csvContent, filename)
        return
      }

      // Handle chart widget export (existing logic)
      if (
        exportData.widgetType === "chart" &&
        exportData.chartData &&
        exportData.splitKeys &&
        exportData.displayNameLookups
      ) {
        const metricId = config.metricId || "unknown"
        const metricDisplayName =
          selectedMetric?.metric_display_name || metricId
        const aggregation = config.aggregation || AGGREGATION_TYPE.AVG

        const csvContent = convertWidgetDataToCSV({
          widgetTitle,
          config,
          chartData: exportData.chartData,
          splitKeys: exportData.splitKeys,
          displayNameLookups: exportData.displayNameLookups,
          dateRange: exportData.dateRange,
          metricDisplayName,
        })

        const filename = generateWidgetCSVFilename(
          widgetTitle,
          metricDisplayName,
          aggregation,
          exportData.dateRange,
        )

        downloadWidgetCSV(csvContent, filename)
      }
    } catch (error) {
      log.error("Failed to download CSV", error, { widgetId: widget.id })
      // Note: notifyError function doesn't exist - removed the call
    }
  }, [widget.config, widget.title, widget.id, selectedMetric])

  const handleWidgetConfigChange = useCallback(
    (newConfig: DashboardWidgetConfig) => {
      onConfigChange?.(widget.id, newConfig)
    },
    [widget.id, onConfigChange],
  )

  const handleExportDataReady = useCallback((data: WidgetExportData | null) => {
    exportDataRef.current = data
    // Only update state when transitioning between having/not having data
    const hasData = !!(
      data &&
      ((data.widgetType === "table" && data.tableData) ||
        (data.widgetType === "chart" && data.chartData))
    )
    setHasExportData(hasData)
  }, [])

  const widgetContent = useMemo(() => {
    if (!React.isValidElement<WidgetContainerChildProps>(children)) {
      return (
        <div className="h-full flex items-center justify-center text-coval-text-muted">
          <Activity className="w-8 h-8" />
        </div>
      )
    }
    return React.cloneElement(children, {
      widget: {
        ...widget,
        metric: selectedDashboardMetric,
        onConfigChange: handleWidgetConfigChange,
      },
      onExportDataReady: handleExportDataReady,
    })
  }, [
    children,
    widget,
    selectedDashboardMetric,
    handleWidgetConfigChange,
    handleExportDataReady,
  ])

  return (
    <>
      <Card
        className="h-full w-full backdrop-blur-sm transition-all duration-200 flex flex-col gap-3 overflow-hidden p-4"
        data-testid={`dashboard-widget-${widget.id}`}
      >
        {widget.type === "text" ? (
          // A text card has no title bar, so the whole card is the bar.
          <WidgetDragBar className="-m-4 flex min-h-0 flex-1 flex-row items-center gap-2 rounded-[inherit] p-4">
            <div className="flex-1 min-w-0 min-h-0 h-full">{widgetContent}</div>

            <div className="flex items-center gap-1 shrink-0 opacity-0 transition-opacity focus-within:opacity-100 group-hover/widget-cell:opacity-100">
              <Button
                icon
                tooltip="Configure"
                tooltipProps={{ side: "top" }}
                variant="ghost"
                className="h-6 w-6 min-w-6"
                data-testid={`dashboard-widget-${widget.id}-configure`}
                onClick={() => onConfigure?.(widget.id)}
                aria-label="Configure widget"
              >
                <Settings className="w-3.5 h-3.5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    icon
                    variant="ghost"
                    className="h-6 w-6 min-w-6"
                    data-testid={`dashboard-widget-${widget.id}-actions`}
                    tooltip="Widget actions"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onWidthChange && (
                    <WidgetWidthMenu
                      widget={widget}
                      onWidthChange={onWidthChange}
                    />
                  )}
                  <DropdownMenuItem
                    data-testid={`dashboard-widget-${widget.id}-clone`}
                    onSelect={() => onClone?.(widget.id)}
                  >
                    <Copy className="w-4 h-4 mr-2 shrink-0" />
                    Clone
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid={`dashboard-widget-${widget.id}-remove`}
                    destructive
                    onSelect={() => setIsRemoveModalOpen(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2 shrink-0" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </WidgetDragBar>
        ) : (
          <WidgetDragBar className="-mx-4 -mt-4 flex shrink-0 flex-row items-center justify-between gap-2 rounded-t-[inherit] px-4 py-2">
            {/*
             * The title is a control, which the grid's sensor would refuse to
             * drag from. Marking it part of the bar lets a tap on it still
             * click and a pull on it still drag.
             */}
            <h3
              className="min-w-0 flex-1 truncate select-none text-sm font-semibold text-coval-text"
              data-widget-drag-through
            >
              {/*
               * The card is named the same way whether or not its metric has
               * arrived — only the link to the metric waits on the fetch, so
               * a loading card still says what it is.
               */}
              {selectedMetric ? (
                <ClickableMetricTitle
                  ref={metricTitleRef}
                  metric={selectedMetric}
                  displayText={widget.config?.title || widget.title}
                  className="text-sm font-semibold hover:text-coval-contrast transition-colors duration-200 cursor-pointer"
                />
              ) : (
                // The title is known from the widget itself, so it reads the
                // same before and after its metric resolves — only the link
                // to the metric's details waits on the lookup.
                <span>{widget.config?.title || widget.title}</span>
              )}
            </h3>

            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/widget-cell:opacity-100">
              <Button
                icon
                tooltip="Configure"
                tooltipProps={{ side: "top" }}
                variant="ghost"
                className="h-6 w-6 min-w-6"
                data-testid={`dashboard-widget-${widget.id}-configure`}
                onClick={() => onConfigure?.(widget.id)}
                aria-label="Configure widget"
              >
                <Settings className="w-3.5 h-3.5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    icon
                    variant="ghost"
                    className="h-6 w-6 min-w-6"
                    data-testid={`dashboard-widget-${widget.id}-actions`}
                    tooltip="Widget actions"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onWidthChange && (
                    <WidgetWidthMenu
                      widget={widget}
                      onWidthChange={onWidthChange}
                    />
                  )}
                  <DropdownMenuItem
                    data-testid={`dashboard-widget-${widget.id}-clone`}
                    onSelect={() => onClone?.(widget.id)}
                  >
                    <Copy className="w-4 h-4 mr-2 shrink-0" />
                    Clone
                  </DropdownMenuItem>
                  {metricId && selectedMetric && (
                    <DropdownMenuItem
                      onSelect={() => metricTitleRef.current?.click()}
                    >
                      <Info className="w-4 h-4 mr-2 shrink-0" />
                      Metric Info
                    </DropdownMenuItem>
                  )}
                  {metricId && onAlert && (
                    <DropdownMenuItem onSelect={() => onAlert(widget.id)}>
                      <Bell className="w-4 h-4 mr-2 shrink-0" />
                      Alert
                    </DropdownMenuItem>
                  )}
                  {hasExportData && (
                    <DropdownMenuItem onSelect={handleDownloadCSV}>
                      <Download className="w-4 h-4 mr-2 shrink-0" />
                      Download CSV
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    data-testid={`dashboard-widget-${widget.id}-remove`}
                    destructive
                    onSelect={() => setIsRemoveModalOpen(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2 shrink-0" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </WidgetDragBar>
        )}

        {widget.type !== "text" && (
          <div className="flex-1 min-w-0 min-h-0">{widgetContent}</div>
        )}
      </Card>

      <Dialog open={isRemoveModalOpen} onOpenChange={setIsRemoveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Widget</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm">
              Are you sure you want to remove this widget? This action cannot be
              undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsRemoveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={`dashboard-widget-${widget.id}-remove-confirm`}
              variant="danger"
              onClick={() => {
                onRemove(widget.id)
                setIsRemoveModalOpen(false)
              }}
            >
              Remove widget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
